import { ReactNode } from 'react'
import { ChevronDown, ChevronUp, Search, SlidersHorizontal } from 'lucide-react'
import { Button } from '@/components/ds2'
import type { LocationData } from '@/features/load-hub/types/filters'
import { formatPickupDate } from '@/utils/format-date'
import { summarizeBoxTruckLocations } from '../utils/boxTruckFilterSummary'
import { BoxTruckRefreshControls } from './BoxTruckRefreshControls'

interface BoxTruckFiltersToolbarProps {
    canRefresh: boolean
    collapsed: boolean
    destinationOptions: LocationData[]
    endDate: Date | null
    equipmentLabels: string[]
    filtersRegionId: string
    hasAdditionalFilters: boolean
    hideLaneFilters: boolean
    loading: boolean
    mobileMoreFiltersOpen: boolean
    onCollapse: () => void
    onExpand: () => void
    onRefresh: () => void
    onSearch: () => void
    originOptions: LocationData[]
    sourceSlot?: ReactNode
    startDate: Date | null
    trailingSlot?: ReactNode
}

const summarizeEquipment = (equipmentLabels: string[]): string => {
    if (equipmentLabels.length === 0) return 'All equipment'
    if (equipmentLabels.length === 1) return equipmentLabels[0]
    return `${equipmentLabels[0]} +${equipmentLabels.length - 1}`
}

const summarizePickup = (startDate: Date | null, endDate: Date | null): string => {
    if (!startDate && !endDate) return 'Pickup date required'
    if (!startDate || !endDate) return 'Pickup date incomplete'
    return formatPickupDate(startDate, endDate, { format: 'MMM D', rangeSeparator: '–' })
}

export const BoxTruckFiltersToolbar = ({
    canRefresh,
    collapsed,
    destinationOptions,
    endDate,
    equipmentLabels,
    filtersRegionId,
    hasAdditionalFilters,
    hideLaneFilters,
    loading,
    mobileMoreFiltersOpen,
    onCollapse,
    onExpand,
    onRefresh,
    onSearch,
    originOptions,
    sourceSlot,
    startDate,
    trailingSlot,
}: BoxTruckFiltersToolbarProps) => {
    const primarySummary = hideLaneFilters
        ? `Driver bids • ${summarizeEquipment(equipmentLabels)}`
        : `${summarizeBoxTruckLocations(originOptions, 'Anywhere')} → ${summarizeBoxTruckLocations(
              destinationOptions,
              'Anywhere'
          )} • ${summarizeEquipment(equipmentLabels)} • ${summarizePickup(startDate, endDate)}`
    const summary = `${primarySummary}${hasAdditionalFilters ? ' • Additional filters active' : ''}`

    return (
        <div className={`flex min-h-11 flex-wrap items-center gap-2 px-3 py-1 min-[900px]:px-4 ${collapsed ? 'bg-background' : 'bg-transparent'}`}>
            {collapsed ? (
                <div className="flex min-w-[180px] flex-1 items-center gap-2" title={summary}>
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-default text-foreground-muted">
                        <SlidersHorizontal size={16} aria-hidden="true" />
                    </span>
                    <span className="min-w-0">
                        <span className="block text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">Filters collapsed</span>
                        <span className="block truncate text-xs font-medium text-foreground">{summary}</span>
                    </span>
                </div>
            ) : (
                sourceSlot && (
                    <div className={`${mobileMoreFiltersOpen ? 'block' : 'hidden'} order-5 w-full min-[900px]:order-none min-[900px]:block min-[900px]:w-auto`}>
                        {sourceSlot}
                    </div>
                )
            )}
            <BoxTruckRefreshControls
                canRefresh={canRefresh}
                hideLaneFilters={hideLaneFilters}
                loading={loading}
                onRefresh={onRefresh}
                className={`${collapsed || mobileMoreFiltersOpen ? 'flex' : 'hidden'} order-5 w-full min-[900px]:order-none min-[900px]:flex min-[900px]:w-auto`}
            />
            <div className="order-6 ml-auto flex w-full items-center justify-end gap-2 min-[900px]:order-none min-[900px]:w-auto">
                {collapsed ? (
                    <>
                        {trailingSlot}
                        {!hideLaneFilters && (
                            <Button variant="primary" size="sm" disabled={loading} onClick={onSearch} prefix={<Search size={15} />}>
                                Search
                            </Button>
                        )}
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={onExpand}
                            suffix={<ChevronDown size={15} />}
                            aria-controls={filtersRegionId}
                            aria-expanded={false}
                        >
                            Show filters
                        </Button>
                    </>
                ) : (
                    <>
                        {trailingSlot}
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={onCollapse}
                            suffix={<ChevronUp size={15} />}
                            aria-controls={filtersRegionId}
                            aria-expanded={true}
                        >
                            Hide filters
                        </Button>
                    </>
                )}
            </div>
        </div>
    )
}
